<template>
  <section
    class="plan-type"
    data-id="own-plan"
    :data-loading="loading ? '1' : '0'"
    :data-frozen-plan="isFrozen ? '1' : '0'"
    :data-has-plan="subscription ? '1' : '0'"
    :data-plan-addons="userAddonsF || '0'"
    :data-plan-type="subscription ? subscription.subscriptionType : null"
    :data-plan-id="subscription ? subscription.subscriptionId : null"
    :data-plan-currency="subscription ? subscription.currency : null"
    :data-plan-external-id="subscription ? subscription.externalPlanId : null"
    :data-subscription-id="subscription ? subscription.id : null"
  >
    <Card
      class="personal-plan"
      :no-card="noCard"
      header-bg-variant="white"
      flush
      :loading="loading"
    >
      <section data-id="plan-type-section">
        <CardBody>
          <div
            class="pb-3"
            v-if="isFrozen && !loading"
            data-id="plan-frozen-warning"
          >
            <AlertCard
              title="Account frozen"
              :variant="Variant.warning"
              :dismissible="false"
              :flat="true"
              :links="[{ to: '/unblock/', text: 'Unfreeze account' }]"
              :subtitle="'Your account is temporarily frozen.'"
            />
          </div>
          <FormGrouping v-if="!isChild">
            <!-- account type -->
            <FormGroup :label="'Account type'" data-id="plan-type">
              <div
                class="flex flex-wrap items-center justify-start gap-2"
                :data-subscription-type="
                  subscription ? subscription.subscriptionType : '0'
                "
              >
                <div class="block lg:inline-block">
                  <span data-id="subscription-type-display">{{
                    subscription ? subscription.subscriptionType : 'Personal'
                  }}</span>
                  <span v-if="subscription" data-id="plan-schedule">
                    <span>{{ subscription.subscriptionSchedule }}</span>
                    <span data-id="plan-currency">{{
                      subscription.currency
                    }}</span>
                  </span>
                </div>
                <div>
                  <Btn
                    tag="NuxtLink"
                    data-id="upgrade-subscription"
                    :variant="Variant.upgrade"
                    icon="arrowRight"
                    icon-position="right"
                    size="sm"
                    to="/upgrade?changePlan=1"
                  >
                    {{
                      subscription ? 'Select different plan' : 'Upgrade plan'
                    }}
                  </Btn>
                </div>
              </div>
            </FormGroup>
            <FormGroup
              v-if="billingPortal && billingPortal.url"
              :label="'Payment settings'"
            >
              <Btn
                tag="a"
                :href="billingPortal.url"
                data-id="billing-portal-link"
                size="sm"
                :variant="Variant.success"
                @click="loading = true"
              >
                {{ 'Open billing portal' }}
              </Btn>
            </FormGroup>
          </FormGrouping>
        </CardBody>
        <!-- plan addons -->
        <CardHeaderWrapper v-if="!isChild" borders has-header>
          <CardHeader title="Addons" icon="addons" />
        </CardHeaderWrapper>
        <CardBody>
          <FormGrouping v-if="!isChild">
            <FormGroup
              :data-has-validation-product="hasProductValidation ? '1' : '0'"
              :label="'Email validation'"
              data-id="application-validation-id"
            >
              <PlanAddonSwitch
                addon="validation"
                :has-addon="hasProductValidation"
                @enable="enableValidationSubscriptionItem"
                @disable="disableValidationSubscriptionItem"
              />
            </FormGroup>
            <FormGroup
              data-el="plan-type-phone-plans"
              :data-has-phone-plan-product="hasPhonePlans ? '1' : '0'"
              :data-phone-plan-countries-enabled="phonePlanCountriesEnabledF"
              :data-phone-plan-countries-disabled="phonePlanCountriesDisabledF"
              :label="'Phone plans'"
              data-id="application-phone-plan-id"
            >
              <PlanAddonSwitch
                addon="phone"
                :has-addon="hasPhonePlans"
                @enable="enablePhonePlans"
                @disable="disablePhonePlans"
                manageTo="/upgrade/phones/"
              />
            </FormGroup>
            <FormGroup
              :data-has-ai-plan-product="hasAIPlans ? '1' : '0'"
              label="AI plans"
              data-id="application-ai-plan-id"
            >
              <PlanAddonSwitch
                addon="ai"
                :has-addon="hasAIPlans"
                @enable="enableAIPlans"
                @disable="disableAIPlans"
              />
            </FormGroup>
            <FormGroup
              :data-has-connector-plan-product="hasConnectorPlans ? '1' : '0'"
              label="Connector plans"
              data-id="application-connector-plan-id"
            >
              <PlanAddonSwitch
                addon="connector"
                :has-addon="hasConnectorPlans"
                @enable="enableConnectorPlans"
                @disable="disableConnectorPlans"
              />
            </FormGroup>
            <FormGroup
              :data-has-guest-plan-product="hasGuestPlans ? '1' : '0'"
              label="Guest plans"
              data-id="application-guest-plan-id"
            >
              <PlanAddonSwitch
                addon="guest"
                :has-addon="hasGuestPlans"
                @enable="enableGuestPlans"
                @disable="disableGuestPlans"
              />
            </FormGroup>
            <FormGroup
              :data-has-inbox-plan-product="hasInboxPlans ? '1' : '0'"
              label="Inbox plans"
              data-id="application-inbox-plan-id"
            >
              <PlanAddonSwitch
                addon="inbox"
                :has-addon="hasInboxPlans"
                @enable="enableInboxPlans"
                @disable="disableInboxPlans"
              />
            </FormGroup>
            <FormGroup
              :data-has-email-plan-product="hasEmailPlans ? '1' : '0'"
              label="Email plans"
              data-id="application-email-plan-id"
            >
              <PlanAddonSwitch
                addon="email"
                :has-addon="hasEmailPlans"
                @enable="enableEmailPlans"
                @disable="disableEmailPlans"
              />
            </FormGroup>

            <FormGroup
              :data-has-user-plan-product="hasUserPlans ? '1' : '0'"
              label="User plans"
              data-id="application-user-plan-id"
            >
              <PlanAddonSwitch
                addon="user"
                :has-addon="hasUserPlans"
                @enable="enableUserPlans"
                @disable="disableUserPlans"
              />
            </FormGroup>
          </FormGrouping>
        </CardBody>
      </section>

      <template #footer>
        <div v-if="!loading">
          <FormDropDown data-id="account-plan-settings" position="top-right">
            <template #button-content>
              <span class="p-0" data-id="plan-action-open">{{
                'Edit plan'
              }}</span>
            </template>
            <FormDropDownItem v-if="!isChild && !isVendor" to="/upgrade">
              {{ 'Upgrade plan' }}
            </FormDropDownItem>
            <FormDropDownItem
              v-if="subscription && !isChild && !isVendor"
              data-id="delete-subscription"
              button
              @click="requestDeleteSubscription"
            >
              {{ 'Cancel subscription' }}
            </FormDropDownItem>
            <FormDropDownItem
              v-if="subscription && !isChild && !isVendor"
              data-id="delete-customer"
              button
              @click="requestDeleteCustomer"
            >
              {{ 'Delete customer' }}
            </FormDropDownItem>
            <FormDropDownItem
              v-if="!isVendor"
              data-id="account-plan-delete-account"
              button
              @click="requestDeleteAccount"
            >
              {{ 'Delete account' }}
            </FormDropDownItem>
          </FormDropDown>
        </div>
      </template>
    </Card>
  </section>
</template>

<script setup lang="ts">
import CardHeader from '../components/CardHeader.vue';
import { navigateTo } from '#imports';
import FormGroup from '../components/form/FormGroup.vue';
import {
  loadFetch,
  loadFetchMany,
} from '../lib/services/helpers';
import { onMounted, ref, computed } from 'vue';
import FormGrouping from '../components/form/FormGrouping.vue';
import Btn from './form/Btn.vue';
import { useStore3 } from '../store/main';
import { storeToRefs } from 'pinia';
import { Variant } from '../store/types';
import Card from '../components/tw/Card.vue';
import FormDropDown from '../components/form/FormDropDown.vue';
import FormDropDownItem from '../components/form/FormDropDownItem.vue';
import CardBody from './tw/CardBody.vue';
import CardHeaderWrapper from './CardHeaderWrapper.vue';
import PlanAddonSwitch from './PlanAddonSwitch.vue';
import AlertCard from '../components/tw/AlertCard.vue';
import {
  AccountDtoAddonsEnum,
  AppendProductAddonPlanAddonEnum,
  PhonePlanDtoPhoneCountryEnum,
} from '@mailslurp/admin-sdk';
import { useNuxtApp } from '#imports';
import { getUpgradePageAddonUrl } from '../lib/services/upgradeLinks';
const { $captureEvent: captureEvent } = useNuxtApp();

const store3 = useStore3();

withDefaults(defineProps<{ noCard?: boolean }>(), {
  noCard: false,
});

const loading = ref(false);

const userAddonsF = computed(() => (userAddons.value ?? []).join(','));
const {
  user,
  offboardingAction,
  subscription,
  phonePlans,
  isVendor,
  isChild,
  billingPortal,
  userPlans,
  userAddons,
} = storeToRefs(store3);
const hasProductValidation = computed(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.VALIDATION) ?? false;
});
const hasInboxPlans = computed<boolean>(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.INBOXES) ?? false;
});
const hasGuestPlans = computed<boolean>(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.GUEST) ?? false;
});
const hasAIPlans = computed<boolean>(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.AI) ?? false;
});
const hasConnectorPlans = computed<boolean>(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.CONNECTORS) ?? false;
});
const hasEmailPlans = computed<boolean>(() => {
  return userAddons.value?.includes(AccountDtoAddonsEnum.EMAILS) ?? false;
});
const hasUserPlans = computed<boolean>(() => {
  return (userPlans.value?.length ?? 0) > 0;
});
const hasPhonePlans = computed<boolean>(() => {
  return (phonePlans.value?.length ?? 0) > 0;
});

const isFrozen = computed<boolean>(() => {
  return user.value?.isFrozen ?? false;
});

const disableGuestPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'guest_plan',
  });
  await loadFetch(() => store3.deleteGuestPlans(), store3, loading);
  await refresh();
};
const enableGuestPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'guest_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.GUEST, {
      expedite: '1',
    }),
  );
};

const disablePhonePlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'phone_plan',
  });
  await loadFetch(() => store3.deletePhonePlans(), store3, loading);
  await refresh();
};
const disableUserPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'user_plan',
  });
  await loadFetch(() => store3.deleteUserPlans(), store3, loading);
  await refresh();
};

const phonePlanCountriesEnabledF = computed(() => {
  return phonePlans.value?.length
    ? phonePlans.value.map((it) => it.phoneCountry).join(',')
    : '0';
});
const phonePlanCountriesDisabledF = computed(() => {
  const existing = phonePlans.value?.map((it) => it.phoneCountry) ?? [];
  const missing = Object.values(PhonePlanDtoPhoneCountryEnum).filter(
    (it) => !existing.includes(it),
  );
  return missing.length ? missing.join(',') : '0';
});

const disableAIPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'ai_plan',
  });
  await loadFetch(() => store3.deleteAIPlans(), store3, loading);
  await refresh();
};

const disableConnectorPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'connector_plan',
  });
  await loadFetch(() => store3.deleteConnectorPlans(), store3, loading);
  await refresh();
};
const disableEmailPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'email_plan',
  });
  await loadFetch(() => store3.deleteEmailPlans(), store3, loading);
  await refresh();
};
const disableInboxPlans = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'inbox_plan',
  });
  await loadFetch(() => store3.deleteInboxPlans(), store3, loading);
  await refresh();
};

const refresh = async () => {
  await loadFetchMany(
    [
      // includes user addons
      () => store3.refreshUserAndSubscription(),
      () => store3.getPhonePlans(),
      () => store3.getUserPlans(),
    ],
    store3,
    loading,
  );
};

const enablePhonePlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'phone_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl('phones', {
      expedite: '1',
    }),
  );
};

const enableUserPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'user_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl('users', {
      expedite: null, // let user select seats
    }),
  );
};
const enableAIPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'ai_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.AI, {
      expedite: '1',
    }),
  );
};
const enableConnectorPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'connector_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.CONNECTORS, {
      expedite: '1',
    }),
  );
};
const enableInboxPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'inbox_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.INBOXES, {
      expedite: '1',
    }),
  );
};
const enableEmailPlans = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'email_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.EMAILS, {
      expedite: '1',
    }),
  );
};

const disableValidationSubscriptionItem = async () => {
  captureEvent('cancel_subscription_addon', {
    addon: 'validation_plan',
  });
  await loadFetch(() => store3.deleteProductValidation(), store3, loading);
  await refresh();
};

const enableValidationSubscriptionItem = async () => {
  captureEvent('click_upgrade_addon_enable', {
    addon: 'validation_plan',
  });
  await navigateTo(
    getUpgradePageAddonUrl(AppendProductAddonPlanAddonEnum.VALIDATION, {
      expedite: '1',
    }),
  );
};

onMounted(async () => {
  await refresh();
  const sub = subscription.value;
  if (sub && sub.demoDaysRemaining == null) {
    await loadFetch(() => store3.getBillingPortal(), store3, loading);
  }
});

const requestDeleteSubscription = async () => {
  offboardingAction.value = 'delete-subscription';
  await navigateTo('/offboarding');
};

const requestDeleteAccount = async () => {
  offboardingAction.value = 'delete-account';
  await navigateTo('/offboarding');
};

const requestDeleteCustomer = async () => {
  offboardingAction.value = 'delete-customer';
  await navigateTo('/offboarding');
};
</script>
